import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { apiService } from '../../services/api';

interface TenantCodeData {
  maintenanceRequestCode: string | null;
  generatedAt: string | null;
  tenant: {
    name: string;
    email: string;
    phone: string;
  } | null;
  property: {
    name: string;
    address: any;
  } | null;
  unit: {
    unitNumber: string;
    name: string;
  } | null;
}

interface TenantCodeState {
  codes: { [rentalUnitUserId: string]: TenantCodeData };
  isLoading: boolean;
  error: string | null;
}

const initialState: TenantCodeState = {
  codes: {},
  isLoading: false,
  error: null,
};

// Async thunks
export const fetchTenantCode = createAsyncThunk(
  'tenantCode/fetch',
  async (rentalUnitUserId: string) => {
    const response = await apiService.get(`/tenant-codes/${rentalUnitUserId}`);
    return { rentalUnitUserId, data: response.data.data };
  }
);

export const generateTenantCode = createAsyncThunk(
  'tenantCode/generate',
  async (rentalUnitUserId: string) => {
    const response = await apiService.post(`/tenant-codes/generate/${rentalUnitUserId}`);
    return { rentalUnitUserId, data: response.data.data };
  }
);

export const validateTenantCode = createAsyncThunk(
  'tenantCode/validate',
  async ({ tenantCode, companyId }: { tenantCode: string; companyId: string }) => {
    const response = await apiService.post('/tenant-codes/validate', {
      tenantCode,
      companyId,
    });
    return response.data;
  }
);

// Slice
const tenantCodeSlice = createSlice({
  name: 'tenantCode',
  initialState,
  reducers: {
    clearTenantCode: (state, action: PayloadAction<string>) => {
      delete state.codes[action.payload];
    },
    clearAllTenantCodes: (state) => {
      state.codes = {};
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // Fetch tenant code
    builder.addCase(fetchTenantCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchTenantCode.fulfilled, (state, action) => {
      state.isLoading = false;
      state.codes[action.payload.rentalUnitUserId] = action.payload.data;
    });
    builder.addCase(fetchTenantCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to fetch tenant code';
    });

    // Generate tenant code
    builder.addCase(generateTenantCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(generateTenantCode.fulfilled, (state, action) => {
      state.isLoading = false;
      state.codes[action.payload.rentalUnitUserId] = action.payload.data;
    });
    builder.addCase(generateTenantCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to generate tenant code';
    });

    // Validate tenant code
    builder.addCase(validateTenantCode.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(validateTenantCode.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(validateTenantCode.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Invalid tenant code';
    });
  },
});

export const { clearTenantCode, clearAllTenantCodes, clearError } = tenantCodeSlice.actions;

export default tenantCodeSlice.reducer;